import {
  CloseOutlined,
  InfoCircleOutlined,
  LeftOutlined
} from '@ant-design/icons'
import { theme } from '@lmc/ui-kit'
import { Button, Heading, Text } from '@lmc/ui-kit/src/general'
import Divider from '@lmc/ui-kit/src/layout/Divider/Divider'
import { Warning } from '@lmc/ui-kit/src/systemIcons'
import { Modal, Tooltip } from 'antd'
import type { ReactNode } from 'react'
import styled from 'styled-components'

export type NavigationModalProps = {
  onConfirmNavigation?: () => void
  onCancelNavigation?: () => void
  modalTitle: string
  modalTitleTooltip?: string
  modalContentText?: string
  modalConfirmText: string
  modalCancelText?: string
  showPrompt: boolean
  confirmNavigation: () => void
  cancelNavigation: () => void
  confirmButtonProps?: any
  hasBackButton?: boolean
  backButtonText?: string
  backNavigation?: () => void
  children?: ReactNode
} & Omit<React.ComponentProps<typeof Modal>, 'onCancel'>

const NavigationModal = ({
  modalTitle,
  modalTitleTooltip,
  modalCancelText,
  modalConfirmText,
  modalContentText,
  showPrompt,
  confirmNavigation,
  cancelNavigation,
  confirmButtonProps,
  children,
  hasBackButton = false,
  backButtonText,
  backNavigation,
  ...props
}: NavigationModalProps) => {
  const modalZIndex = (props as { zIndex?: number }).zIndex ?? 9999
  const tooltipZIndex = modalZIndex + 1

  const handleConfirmNavigation = () => {
    props.onConfirmNavigation?.()
    confirmNavigation()
  }
  const handleCancelNavigation = () => {
    cancelNavigation?.()
    props.onCancelNavigation?.()
  }

  return (
    <Modal
      open={showPrompt}
      onCancel={cancelNavigation}
      zIndex={modalZIndex}
      footer={[
        <ModalFooter>
          <FooterButtons hasBackButton={hasBackButton}>
            {hasBackButton && (
              <Button variant="text" onClick={backNavigation}>
                <LeftOutlined />
                {backButtonText}
              </Button>
            )}
            <RightSide>
              {modalCancelText && (
                <Button onClick={handleCancelNavigation}>
                  <CloseOutlined />
                  {modalCancelText}
                </Button>
              )}
              <Button
                onClick={handleConfirmNavigation}
                variant="danger"
                {...confirmButtonProps}
              >
                {modalConfirmText}
              </Button>
            </RightSide>
          </FooterButtons>
        </ModalFooter>
      ]}
      {...props}
    >
      <ConfirmationModalContent>
        <ConfirmationModalTitleContainer>
          <Warning color={theme.color.status.warning} size={24} />
          <TitleRow>
            <Heading level={4}>{modalTitle}</Heading>
            {modalTitleTooltip && (
              <Tooltip title={modalTitleTooltip} zIndex={tooltipZIndex}>
                <InfoCircleOutlined
                  tabIndex={0}
                  aria-label={modalTitleTooltip}
                />
              </Tooltip>
            )}
          </TitleRow>
        </ConfirmationModalTitleContainer>
        <Divider />
        {modalContentText && <Text> {modalContentText}</Text>}
        {children}
        <Divider />
      </ConfirmationModalContent>
    </Modal>
  )
}

export default NavigationModal

const ModalFooter = styled.div`
  margin-top: 12px;
  width: 100%;
  display: flex;
  justify-content: flex-end;
`

const FooterButtons = styled.div<{ hasBackButton: boolean }>`
  width: ${({ hasBackButton }) => (hasBackButton ? '100%' : 'auto')};
  display: flex;
  justify-content: space-between;
`

const RightSide = styled.div`
  display: flex;
  gap: 10px;
`

const ConfirmationModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  color: ${(props) => props.theme.color.neutral.color3};
`

const ConfirmationModalTitleContainer = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
`

const TitleRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`
